import styled from '@emotion/styled';
import { Box } from '@zoopla-eng/zds-components/box';
import { ButtonLeft } from '@zoopla-eng/zds-components/button';
import { AlertMedium, SaveMedium } from '@zoopla-eng/zds-components/icons';
import {
    ADBRIDG_CLASS,
    AdvertProvider,
    AdvertSlotDfp,
    AD_UNIT_PAGE,
    AD_UNIT_ROOTS,
    DfpTargeting,
    DFP_AD_UNIT,
    getPageSectionForAdUnit
} from '@zoopla/adverts/dfp';
import { useContainerMeasurement } from '@zoopla/dom-util';
import {
    AnalyticsTaxonomy,
    Links,
    SeoLinks
} from '@zoopla/listing-search-context';
import { SeoLinksComponent } from '@zoopla/listing-search-seo-links';
import {
    AlertPanel,
    AlertPanelMobile,
    SaveThisSearchModalWithAuth,
    trackCreateEmailAlert,
    trackSaveThisSearch,
    useSaveThisSearchModal
} from '@zoopla/listing-search/alert-panel';
import { useOptimizelyFeatureFlag } from '@zoopla/optimizely';
import { getUserSelector, useIsLoggedIn } from '@zoopla/redux-reducer-user';
import {
    ANALYTICS_BASE_TAXONOMY,
    Taxonomy,
    updateUserTaxonomyKeys,
    UserTaxonomy,
    useTrackSegmentEvent
} from '@zoopla/tracking';
import { useRouter } from 'next/router';
import * as React from 'react';
import { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AlertCtaModal } from './AlertCtaModal';
import { InnerWrapper, SearchResultsWrapper, SideBar } from './Layout.styles';
import { QuickFilters } from './QuickFilters';
import SearchAndFiltersWrapper from './SearchAndFiltersWrapper';

const Section = styled.section`
    width: 100%;
`;

export type SearchResultsLayout = {
    dfpAdTargeting: DfpTargeting;
    seoAccordions: SeoLinks[];
    numberOfRegularListings: number;
    pageNumber?: number;
    links: Links;
    children: React.ReactNode;
    analyticsTaxonomy?: AnalyticsTaxonomy;
};

const SearchResultsLayout: FC<React.PropsWithChildren<SearchResultsLayout>> = ({
    dfpAdTargeting,
    seoAccordions,
    children,
    pageNumber,
    links,
    analyticsTaxonomy
}) => {
    const SKYSCRAPER_AD_HEIGHT = 600;
    const isLoggedIn = useIsLoggedIn();
    const { route, pathname } = useRouter();

    const adUnitPage = getPageSectionForAdUnit(route);

    const [adProviderVal, updateAdProviderVal] = useState({
        showPlaceHolderContent: false,
        targeting: {
            ...dfpAdTargeting,
            signed_in_status: isLoggedIn ? 'signed in' : 'signed out'
        },
        adBridgPage: adUnitPage
    });

    useEffect(() => {
        updateAdProviderVal({
            showPlaceHolderContent: false,
            targeting: {
                ...dfpAdTargeting,
                signed_in_status: isLoggedIn ? 'signed in' : 'signed out'
            },
            adBridgPage: adUnitPage
        });
    }, [dfpAdTargeting, isLoggedIn, pageNumber, adUnitPage]);

    const sidebarRef = useRef<HTMLDivElement>(null);
    const stickyContainerRef = useRef<HTMLDivElement>(null);
    const [, sidebarHeight] = useContainerMeasurement(sidebarRef);
    const [stickyContainerHeight, setStickyContainerHeight] = useState('auto');
    const [disableSticky, setDisableSticky] = useState(false);

    useEffect(() => {
        const stickyContainerYPosition =
            stickyContainerRef.current?.offsetTop || 0;
        const numberOfSkyscraperAds = 2;
        const margin = 32;
        const verticalSpace = sidebarHeight - stickyContainerYPosition;

        setDisableSticky(
            verticalSpace <=
                (SKYSCRAPER_AD_HEIGHT + margin) * numberOfSkyscraperAds
        );
        setStickyContainerHeight(
            disableSticky ? 'auto' : Math.floor(verticalSpace / 2) + 'px'
        );
    }, [sidebarHeight, stickyContainerHeight, disableSticky]);

    const [isSaveEmailAlertModalEnabled] = useOptimizelyFeatureFlag(
        'sr_1249_save_email_alert_modal'
    );

    const [isLsrpNewHomesQuickFilterEnabled] = useOptimizelyFeatureFlag(
        'lsrp_new_homes_quick_filter'
    );

    const isForSale = pathname.match(/for-sale|new-homes/);

    const {
        showAuthModal,
        setShowAuthModal,
        defaultAlertFrequency,
        setDefaultAlertFrequency,
        showSaveThisSearchModal,
        setShowSaveThisSearchModal
    } = useSaveThisSearchModal();

    const trackSegmentEvent = useTrackSegmentEvent();
    const userTaxonomy = useSelector(getUserSelector);

    return (
        <AdvertProvider value={adProviderVal}>
            <SearchAndFiltersWrapper>
                {isLsrpNewHomesQuickFilterEnabled && isForSale ? (
                    <QuickFilters />
                ) : null}

                <SearchResultsWrapper suppressHydrationWarning={true}>
                    {isSaveEmailAlertModalEnabled ? (
                        <Box
                            display="flex"
                            justifyContent="space-evenly"
                            pb="$S400"
                            bpM100={{ pb: 0 }}
                            bpL100={{ display: 'none' }}
                        >
                            <SaveThisSearchModalWithAuth
                                {...{
                                    showAuthModal,
                                    setShowAuthModal,
                                    defaultAlertFrequency,
                                    setDefaultAlertFrequency,
                                    showSaveThisSearchModal,
                                    setShowSaveThisSearchModal
                                }}
                            />

                            <ButtonLeft
                                category="tertiary"
                                icon={<AlertMedium />}
                                onClick={() => {
                                    setDefaultAlertFrequency('0');

                                    trackCreateEmailAlert();

                                    trackSegmentEvent(
                                        'Click Email Alert',
                                        {
                                            ...ANALYTICS_BASE_TAXONOMY,
                                            ...(analyticsTaxonomy as unknown as Taxonomy),
                                            ...(updateUserTaxonomyKeys(
                                                userTaxonomy
                                            ) as unknown as UserTaxonomy),
                                            signedInStatus: isLoggedIn
                                                ? 'signed in'
                                                : 'signed out',
                                            location:
                                                analyticsTaxonomy?.areaName
                                        },
                                        undefined
                                    );

                                    if (!isLoggedIn) {
                                        setShowAuthModal(true);
                                    } else {
                                        setShowSaveThisSearchModal(true);
                                    }
                                }}
                            >
                                Create alert
                            </ButtonLeft>

                            <ButtonLeft
                                category="tertiary"
                                icon={<SaveMedium />}
                                onClick={() => {
                                    setDefaultAlertFrequency('');

                                    trackSaveThisSearch();

                                    trackSegmentEvent(
                                        'Click Save Search',
                                        {
                                            ...ANALYTICS_BASE_TAXONOMY,
                                            ...(analyticsTaxonomy as unknown as Taxonomy),
                                            ...(updateUserTaxonomyKeys(
                                                userTaxonomy
                                            ) as unknown as UserTaxonomy),
                                            signedInStatus: isLoggedIn
                                                ? 'signed in'
                                                : 'signed out',
                                            location:
                                                analyticsTaxonomy?.areaName
                                        },
                                        undefined
                                    );

                                    if (!isLoggedIn) {
                                        setShowAuthModal(true);
                                    } else {
                                        setShowSaveThisSearchModal(true);
                                    }
                                }}
                            >
                                Save search
                            </ButtonLeft>
                        </Box>
                    ) : (
                        <AlertPanelMobile
                            links={links}
                            taxonomy={analyticsTaxonomy}
                        />
                    )}

                    <InnerWrapper>
                        <Section data-testid="search-content">
                            {children}
                        </Section>
                        <SideBar
                            data-testid="search-sidebar"
                            ref={sidebarRef}
                            isStickyDesktopBar
                        >
                            <AlertPanel
                                links={links}
                                analyticsTaxonomy={analyticsTaxonomy}
                            />
                            <AdvertSlotDfp
                                uuid={'pn' + pageNumber + '_sb1'}
                                position={0}
                                showOnly={['>', 1140]}
                                sizes={[[300, 16], 'fluid']}
                                adUnitCode={[
                                    AD_UNIT_ROOTS.TEXT_LINK,
                                    getPageSectionForAdUnit(route),
                                    AD_UNIT_PAGE.LSRP
                                ]}
                                adbridgClass={ADBRIDG_CLASS.TEXT_LINK_LSRP}
                            />
                            <AdvertSlotDfp
                                uuid={'pn' + pageNumber + '_sb2'}
                                position={0}
                                showOnly={['>', 1140]}
                                sizes={[[300, 250]]}
                                adUnitCode={[AD_UNIT_ROOTS.MPU, adUnitPage]}
                                adbridgClass={ADBRIDG_CLASS.MPU}
                            />
                            <AdvertSlotDfp
                                uuid={'pn' + pageNumber + '_sb3'}
                                position={0}
                                showOnly={['>', 1140]}
                                sizes={[[300, 120], 'fluid']}
                                adUnitCode={DFP_AD_UNIT.VALUATION_BOOSTER}
                                adbridgClass={ADBRIDG_CLASS.VALUATION_BOOSTER}
                            />
                            <AdvertSlotDfp
                                uuid={'pn' + pageNumber + '_sb4'}
                                position={1}
                                showOnly={['>', 1140]}
                                sizes={[[300, 250]]}
                                adUnitCode={[AD_UNIT_ROOTS.MPU_2, adUnitPage]}
                                adbridgClass={ADBRIDG_CLASS.MPU_2}
                            />

                            {!!seoAccordions.length && <SeoLinksComponent />}

                            <div
                                id="adb_ss_target"
                                data-testid="adbridge-sidebar"
                            />
                        </SideBar>
                    </InnerWrapper>
                </SearchResultsWrapper>
                <AlertCtaModal links={links} />
            </SearchAndFiltersWrapper>
        </AdvertProvider>
    );
};

export default SearchResultsLayout;
